/**
 * 金額進位策略
 * 統一處理所有金額進位，避免帳目差 1 元
 */
import { Decimal } from 'decimal.js';
import { RoundingMode } from './types.js';
/**
 * 將金額進位到整數
 *
 * @param amount 金額（Decimal 或 number）
 * @param mode 進位模式，預設為四捨五入
 * @returns 進位後的金額（Decimal）
 *
 * @example
 * ```ts
 * roundMoney(new Decimal(11.4), RoundingMode.ROUND) // 11
 * roundMoney(new Decimal(11.5), RoundingMode.ROUND) // 12
 * roundMoney(new Decimal(11.4), RoundingMode.FLOOR)  // 11
 * roundMoney(new Decimal(11.4), RoundingMode.CEIL)   // 12
 * ```
 */
export function roundMoney(amount, mode = RoundingMode.ROUND) {
    const value = new Decimal(amount);
    switch (mode) {
        case RoundingMode.ROUND:
            return value.round();
        case RoundingMode.FLOOR:
            return value.floor();
        case RoundingMode.CEIL:
            return value.ceil();
        default:
            return value.round();
    }
}
/**
 * 將金額進位到指定小數位數
 *
 * @param amount 金額（Decimal 或 number）
 * @param decimals 小數位數，預設為 0（整數）
 * @param mode 進位模式，預設為四捨五入
 * @returns 進位後的金額（Decimal）
 *
 * @example
 * ```ts
 * roundMoneyToDecimals(new Decimal(11.456), 2, RoundingMode.ROUND) // 11.46
 * roundMoneyToDecimals(new Decimal(11.456), 2, RoundingMode.FLOOR) // 11.45
 * ```
 */
export function roundMoneyToDecimals(amount, decimals = 0, mode = RoundingMode.ROUND) {
    const value = new Decimal(amount);
    const multiplier = new Decimal(10).pow(decimals);
    switch (mode) {
        case RoundingMode.ROUND:
            return value.mul(multiplier).round().div(multiplier);
        case RoundingMode.FLOOR:
            return value.mul(multiplier).floor().div(multiplier);
        case RoundingMode.CEIL:
            return value.mul(multiplier).ceil().div(multiplier);
        default:
            return value.mul(multiplier).round().div(multiplier);
    }
}
